import React from "react"
import { GatsbyImage } from "gatsby-plugin-image"
import { graphql, useStaticQuery } from "gatsby"
import { useTranslation } from "gatsby-plugin-react-i18next"

import { Element, Image, Text, ElementProps } from "legacy/components/shared/base"
import { links } from "src/constants"
import useBreakpoints from "src/legacy/hooks/useBreakpoints"

interface OwnProps {
  desktopAlignment: "left" | "center"
  mobileAlignment: "left" | "center"
  tabletAlignment?: "left" | "center"
  useQRCode?: boolean
}

export type AppStoreButtonsProps = OwnProps & ElementProps

const AppStoreButtons: React.FC<AppStoreButtonsProps> = ({
  desktopAlignment,
  mobileAlignment,
  tabletAlignment,
  useQRCode = false,
}): React.ReactElement => {
  const { breakpoint } = useBreakpoints()
  const imageQuery = useStaticQuery(graphql`
    query AppStoreButtons {
      apple: file(
        relativePath: { eq: "legacy/appStoreButtons/apple-app-store-badge.svg" }
      ) {
        publicURL
      }
      googlePlay: file(
        relativePath: { eq: "legacy/appStoreButtons/google-play-badge.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FIXED, width: 190)
        }
      }
      qrCode: file(
        relativePath: { eq: "legacy/appStoreButtons/b8nu581a_qr_code.png" }
      ) {
        childImageSharp {
          gatsbyImageData
        }
      }
    }
  `)
  const { t } = useTranslation("shared", {
    keyPrefix: "app_store_buttons",
  })

  const addAnalytics = (platform: string): void => {
    analytics.track("Download Button Clicked", {
      os: platform,
    })
  }

  const desktop = desktopAlignment === "left" ? "flex-start" : "center"
  const mobile = mobileAlignment === "left" ? "flex-start" : "center"
  const tablet = tabletAlignment === "left" ? "flex-start" : mobile

  const appleStoreLink = links.appleAppStore
  const googleStoreLink = links.googlePlayStore

  const isDesktop = breakpoint !== "sm"
  const isQRCodeInDesktop = useQRCode && isDesktop

  return (
    <Element display={isQRCodeInDesktop ? "flex" : "block"}>
      {isQRCodeInDesktop && (
        <Element mr="20">
          <GatsbyImage
            alt="QR Code"
            image={imageQuery.qrCode.childImageSharp.gatsbyImageData}
          />
        </Element>
      )}
      <Element
        alignItems="center"
        alignSelf="center"
        display={isQRCodeInDesktop ? "inline-flex" : "flex"}
        flexWrap="wrap"
        justifyContent={[mobile, tablet, desktop]}
        width={isQRCodeInDesktop ? "50%" : "100%"}
      >
        <Text
          as={isQRCodeInDesktop ? "div" : "a"}
          display="inline-block"
          fontSize="0"
          href={appleStoreLink}
          mr="15px"
          onClick={() => addAnalytics("Apple")}
          overflow="hidden"
          width="150px"
        >
          <Image
            height="50px"
            imageData={{
              alt: t("apple_alt"),
              url: imageQuery.apple.publicURL,
            }}
            width="150px"
          />
        </Text>
        <Text
          as={isQRCodeInDesktop ? "div" : "a"}
          display="inline-block"
          fontSize="0"
          href={googleStoreLink}
          ml="-12px"
          onClick={() => addAnalytics("Android")}
          width="190px"
        >
          <GatsbyImage
            alt={t("google_alt")}
            image={imageQuery.googlePlay.childImageSharp.gatsbyImageData}
          />
        </Text>
      </Element>
    </Element>
  )
}

export default AppStoreButtons
